import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { GraphQuery, MyResource, MyResourceKey, Vulnerability } from '@/types';
import { UseExtendInfiniteQueryOptions, useInfiniteQuery } from '@/utils/api';

interface UseMyProps<
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
> {
  resource: ResourceKey;
  // query to filter the resource data, if not provided, it will fetch all data
  query?: string | GraphQuery;
  to?: string;
  desc?: boolean;
  global?: boolean;
  tree?: Tree;
  allTenants?: boolean;
}

interface VulnerabilityResponse {
  vulnerabilities: Vulnerability[];
  count: number;
}

export type UseMyOptions<
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
> = UseExtendInfiniteQueryOptions<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Tree extends true ? any[] : MyResource[ResourceKey]
> & {
  doNotImpersonate?: boolean;
  email?: string;
};

export const useMy = <
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
>(
  props: UseMyProps<ResourceKey, Tree>,
  options?: UseMyOptions<ResourceKey, Tree>
) => {
  const axios = useAxios({
    doNotImpersonate: options?.doNotImpersonate,
    email: options?.email,
  });

  // Ensure desc is true for statistics resource
  const desc = props.resource === 'statistic' ? true : props.desc;

  const response = useInfiniteQuery<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Tree extends true ? any[] : MyResource[ResourceKey]
  >({
    ...options,
    defaultErrorMessage: `Failed to fetch ${props.resource} data`,
    retry: false,
    queryKey: useGetUserKey(
      getQueryKey.getMy(
        props.resource,
        JSON.stringify(props.query),
        props.to,
        desc,
        props.global,
        props.allTenants
      ),
      options?.doNotImpersonate,
      options?.email
    ),
    queryFn: async ({ pageParam }) => {
      // Add user=true for conversation and message resources
      const needsUserScope =
        props.resource === 'conversation' || props.resource === 'message';

      const { data } =
        typeof props.query === 'object'
          ? await axios({
              method: 'POST',
              url: '/my',
              params: {
                tree: props.tree,
                label: props.resource,
                ...(needsUserScope ? { user: 'true' } : {}),
                ...(props.allTenants ? { allTenants: props.allTenants } : {}),
              },
              data: {
                ...props.query,
                page: pageParam ? pageParam : undefined,
              },
            })
          : await axios.get(`/my`, {
              params: {
                label: props.resource,
                key:
                  props.query && props.query.startsWith('conversation:')
                    ? props.query
                    : `#${props.resource}${props.query ? (props.query.startsWith('#') ? props.query : '#' + props.query) : ''}`,
                to: props.to,
                ...(desc ? { desc } : {}),
                ...(props.global ? { global: props.global } : {}),
                ...(needsUserScope ? { user: 'true' } : {}),
                offset: pageParam ? JSON.stringify(pageParam) : undefined,
                ...(props.allTenants ? { allTenants: props.allTenants } : {}),
              },
            });

      if (props.tree) {
        const treeData = data === null ? [] : data;
        const limitPerPage =
          typeof props.query === 'object' ? props.query?.limit || 100 : 0;

        return {
          data: treeData,
          offset:
            treeData.length >= limitPerPage
              ? data?.offset || (pageParam ? Number(pageParam) + 1 : 1)
              : undefined,
        };
      }

      // Handle GraphQuery responses differently from string queries
      if (typeof props.query === 'object') {
        return handleGraphQueryResponse(data, props.resource);
      }

      const resourceData =
        props.resource === 'technology'
          ? data['technologies'] || []
          : props.resource === 'event'
            ? data['traceevents'] || []
            : data[`${props.resource}s`] || [];

      if (props.resource === 'vulnerability') {
        const vulnResponse = data as VulnerabilityResponse;
        return {
          data: vulnResponse.vulnerabilities,
          offset: undefined,
        };
      }

      if (props.resource === 'asset') {
        return {
          data: resourceData || [],
          offset: data?.offset,
        };
      }

      if (props.resource === 'seed') {
        return {
          data: resourceData || [],
          offset: data?.offset,
        };
      }

      return { data: resourceData, offset: data?.offset };
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      return lastPage.offset;
    },
  });

  const processedData = response.data
    ? response.data.pages.map(({ data }) => data).flat()
    : [];

  return {
    ...response,
    data: processedData,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleGraphQueryResponse(data: any, resource: MyResourceKey) {
  // For GraphQuery, the response is a GraphQueryResponse
  const graphResponse = data as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Extract data based on the resource type
  let resourceData: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

  switch (resource) {
    case 'asset':
      resourceData = [
        ...(graphResponse.assets || []),
        ...(graphResponse.adobjects || []),
        ...(graphResponse.addomains || []),
        ...(graphResponse.integrations || []),
        ...(graphResponse.repositories || []),
        ...(graphResponse.webapplications || []),
        ...(graphResponse.awsresources || []),
        ...(graphResponse.azureresources || []),
        ...(graphResponse.gcpresources || []),
      ];
      break;
    case 'attribute':
      resourceData = graphResponse.attributes || [];
      break;
    case 'seed':
      resourceData = [
        ...(graphResponse.seeds ? Object.values(graphResponse.seeds) : []),
        ...(graphResponse.webapplications || []),
      ];
      break;
    case 'preseed':
      resourceData = graphResponse.preseeds || [];
      break;
    case 'webpage':
      resourceData = graphResponse.webpages || [];
      break;
    case 'webapplication':
      resourceData = graphResponse.webapplications || [];
      break;
    case 'technology':
      resourceData = graphResponse.technologies || [];
      break;
    case 'risk':
      resourceData = graphResponse.risks || [];
      break;
    case 'vulnerability':
      resourceData = graphResponse.vulnerabilities || [];
      break;
    case 'addomain':
      resourceData = graphResponse.adobjects || [];
      break;
    case 'parkeddomain':
      resourceData = graphResponse.parkeddomains || [];
      break;
    default:
      // Try to get data using the resource name pattern as fallback
      resourceData = graphResponse[`${resource}s`] || [];
  }

  return {
    data: resourceData,
    offset: graphResponse.offset,
  };
}

export const useMyAllTenants = <
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
  TransformResult = any,
>(
  props: UseMyProps<ResourceKey, Tree>,
  transform?: (data: TransformResult[]) => Map<string, TransformResult>
) => {
  const query = useMy({
    ...props,
    allTenants: true,
  });

  const dataMap = transform
    ? transform(query.data)
    : new Map(query.data?.map(item => [item.username, item]));

  return {
    ...query,
    dataMap,
  };
};
